import { logger } from '@automata/utils';
import { createSelector } from '@reduxjs/toolkit';
import hasCycle from 'graphology-dag/has-cycle';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import uniqWith from 'lodash/uniqWith';
import { Edge } from 'reactflow';

import { NEW_TASK_NODE_Y_OFFSET } from './constants';
import type { GraphState, NodePositionById } from './graphSlice';
import { createGraphologyGraph } from './helpers';
import {
  DATA_DEPENDENCY_EDGE,
  LABWARE_DEPENDENCY_EDGE,
  TASK_DEPENDENCY_EDGE,
} from '../../constants';
import type { RootState } from '../../store';
import {
  type GraphEdges,
  type GraphNodes,
  type LabwareNode,
  type NodePosition,
  NodeType,
  type SelectedNode,
  type TaskNode,
} from '../../types';
import { getPositionsCenter } from '../../utils';
import { toTaskDependencyHandleId } from '../helpers';
import { selectTaskDependenciesAreVisible } from '../ui/selectors';
import {
  selectLabwareProperties,
  selectTaskPropertiesPreview,
} from '../workflow/selectors';

export const selectSlice = (state: RootState): GraphState =>
  state.undoableState.present.graph;

const _selectLabwareNodes = createSelector(
  selectSlice,
  (slice): NodePositionById => slice.labwareNodes
);
const _selectTaskNodes = createSelector(
  selectSlice,
  (slice): NodePositionById => slice.taskNodes
);

export const selectSelectedNodeIds = createSelector(
  selectSlice,
  (slice): string[] => slice.selectedNodes
);

export const selectSelectedEdges = createSelector(
  selectSlice,
  (slice): string[] => slice.selectedEdges
);

export const selectLabwareDependencyEdges = createSelector(
  selectSlice,
  selectSelectedEdges,
  (slice, selectedEdges): GraphEdges => {
    return slice.labwareDependencyEdges.map((edge) => ({
      ...edge,
      type: LABWARE_DEPENDENCY_EDGE,
      selected: selectedEdges.includes(edge.id),
    }));
  }
);

export const selectDataHandleIsConnectedByHandleID = createSelector(
  selectSlice,
  (slice): { [handleID: string]: boolean } => {
    return slice.dataDependencyEdges.reduce<{ [handleID: string]: boolean }>(
      (prev, curr) => {
        if (!curr.sourceHandle || !curr.targetHandle) {
          return prev;
        }
        return {
          ...prev,
          [curr.sourceHandle]: true,
          [curr.targetHandle]: true,
        };
      },
      {}
    );
  }
);

export const selectDataDependencyEdges = createSelector(
  selectSlice,
  selectSelectedEdges,
  (slice, selectedEdges): GraphEdges => {
    return slice.dataDependencyEdges.map((edge) => ({
      ...edge,
      type: DATA_DEPENDENCY_EDGE,
      selected: selectedEdges.includes(edge.id),
    }));
  }
);

export const selectTaskDependencies = createSelector(
  selectSlice,
  (slice) => slice.taskDependencies
);

export const selectTimeConstraintInstances = createSelector(
  selectSlice,
  (slice) => slice.timeConstraints
);

export const selectSelectedTimeConstraintIdForPropertiesSidebar =
  createSelector(
    selectSelectedEdges,
    selectTimeConstraintInstances,
    (selectedEdges, timeConstraints): string | null => {
      const edgeId = selectedEdges[selectedEdges.length - 1];

      if (edgeId === undefined) return null;

      return timeConstraints[edgeId]?.id ?? null;
    }
  );

export const selectNodeIdToColorIndex = createSelector(
  selectSlice,
  (slice) => slice.nodeIdToColorIndex
);

// Edges (labware and data) where a target node receives multiple incoming edges
// Returns a mapping of targetHandle to the array of edges that share that targetHandle

export const selectLabwareEdgesWithDuplicatedTarget = createSelector(
  selectLabwareDependencyEdges,
  (edges): Record<string, GraphEdges> => {
    const withTargetHandle = edges.filter((e) => e.targetHandle != null);

    const grouped: Record<string, GraphEdges> = {};

    // group by targetHandle
    for (const e of withTargetHandle) {
      const key = e.targetHandle!;
      (grouped[key] ??= []).push(e);
    }

    // filter out groups with only one edge
    const result: Record<string, GraphEdges> = {};
    for (const [targetHandle, group] of Object.entries(grouped)) {
      if (group.length > 1) {
        result[targetHandle] = group;
      }
    }

    return result;
  }
);

/** discard edge handle info and get only the unique source:target edges */
export const _selectUniqueLabwareDependencyEdges = createSelector(
  selectLabwareDependencyEdges,
  (edges): Array<{ source: string; target: string }> => {
    const sourceTargetEdges = edges.map(({ source, target }) => ({
      source,
      target,
    }));
    return uniqWith(sourceTargetEdges, isEqual);
  }
);

export const selectTaskDependencyEdges = createSelector(
  selectTaskDependencies,
  selectSelectedEdges,
  selectTaskDependenciesAreVisible,
  selectSelectedNodeIds,
  (
    taskDependencies,
    selectedEdges,
    taskDependenciesAreVisible,
    selectedNodeIds
  ): Edge[] => {
    const selectedNodeSet = new Set(selectedNodeIds);

    return Object.values(taskDependencies).map(
      ({ id, fromTaskId, toTaskId }) => ({
        id,
        source: fromTaskId,
        target: toTaskId,
        sourceHandle: toTaskDependencyHandleId('source', fromTaskId),
        targetHandle: toTaskDependencyHandleId('target', toTaskId),
        type: TASK_DEPENDENCY_EDGE,
        selected: selectedEdges.includes(id),
        hidden:
          !taskDependenciesAreVisible &&
          !(selectedNodeSet.has(toTaskId) || selectedNodeSet.has(fromTaskId)),
      })
    );
  }
);

export const selectLabwareSlotMappingByTaskId = createSelector(
  selectSlice,
  (slice) => slice.labwareSlotMappingByTask
);

export const selectHandlePairIdOrderByNode = createSelector(
  selectSlice,
  (slice) => slice.handlePairIdOrderByNode
);

export const selectAllNodePositions = createSelector(
  _selectLabwareNodes,
  _selectTaskNodes,
  (labware, tasks): NodePositionById => {
    return { ...labware, ...tasks };
  }
);

export const selectSelectedNodes = createSelector(
  _selectLabwareNodes,
  _selectTaskNodes,
  selectSelectedNodeIds,
  (labwareNodes, taskNodes, selectedNodeIds): SelectedNode[] => {
    return selectedNodeIds.flatMap((id): SelectedNode[] => {
      if (id in labwareNodes) {
        return [{ nodeType: NodeType.LABWARE, id }];
      }
      if (id in taskNodes) {
        return [{ nodeType: NodeType.TASK, id }];
      }
      logger.warn(
        'Unexpected state: non-null selectedNode does not exist in labwareNodes or taskNodes',
        id
      );
      return [];
    });
  }
);

export const selectSelectedNodeForPropertiesSidebar = createSelector(
  selectSelectedNodes,
  (selectedNodes): SelectedNode | null => {
    const firstSelectedNode = selectedNodes[0];
    if (selectedNodes.length !== 1 || firstSelectedNode === undefined) {
      return null;
    }

    const { nodeType, id } = firstSelectedNode;
    return { nodeType, id };
  }
);

export const selectSelectedNodePosition = createSelector(
  _selectLabwareNodes,
  _selectTaskNodes,
  selectSelectedNodeIds,
  (labwareNodes, taskNodes, selectedNodeIds): NodePosition | null => {
    const numNodes = selectedNodeIds.length;

    if (numNodes === 0) {
      return null;
    }

    return getPositionsCenter({
      positions: { ...labwareNodes, ...taskNodes },
      selectedNodeIds,
    });
  }
);

export const selectPositionForNewTaskNode = createSelector(
  selectSelectedNodePosition,
  (selectedNodePosition) => {
    if (selectedNodePosition != undefined) {
      // Place new node below selected node
      return {
        x: selectedNodePosition.x,
        y: selectedNodePosition.y + NEW_TASK_NODE_Y_OFFSET,
      };
    }
    return null;
  }
);

export const selectAllUniqueNodePositions = createSelector(
  _selectLabwareNodes,
  _selectTaskNodes,
  (labwareNodes, taskNodes): NodePosition[] => {
    return uniqWith(
      [...Object.values(labwareNodes), ...Object.values(taskNodes)],
      isEqual
    );
  }
);

export const _selectUniqueTaskAndLabwareAndDataDependencyEdges = createSelector(
  selectTaskDependencyEdges,
  selectLabwareDependencyEdges,
  selectDataDependencyEdges,
  (
    taskEdges,
    labwareEdges,
    dataDependencyEdges
  ): Array<{
    source: string;
    target: string;
  }> =>
    uniqWith([...taskEdges, ...labwareEdges, ...dataDependencyEdges], isEqual)
);

export const selectGraphologyGraphWithLabwareDependencies = createSelector(
  _selectLabwareNodes,
  _selectTaskNodes,
  _selectUniqueLabwareDependencyEdges,
  selectTaskPropertiesPreview,
  selectLabwareProperties,
  createGraphologyGraph
);

export const selectGraphologyGraphWithTaskAndLabwareAndDataDependencies =
  createSelector(
    _selectLabwareNodes,
    _selectTaskNodes,
    _selectUniqueTaskAndLabwareAndDataDependencyEdges,
    selectTaskPropertiesPreview,
    selectLabwareProperties,
    createGraphologyGraph
  );

export const selectGraphHasCycle = createSelector(
  selectGraphologyGraphWithLabwareDependencies,
  (graph) => hasCycle(graph)
);

/** These GraphNodes are used to populate the React Flow graph. */
export const selectGraphNodes = createSelector(
  _selectLabwareNodes,
  _selectTaskNodes,
  selectSelectedNodeIds,
  (labwareNodes, taskNodes, selectedNodes): GraphNodes => {
    const labware = Object.entries(labwareNodes).map(
      ([id, position]): LabwareNode => {
        return {
          id,
          data: null,
          position,
          type: 'labware',
          selected: selectedNodes.includes(id),
        };
      }
    );

    const tasks = Object.entries(taskNodes).map(([id, position]): TaskNode => {
      return {
        id,
        position,
        data: null,
        type: 'task',
        selected: selectedNodes.includes(id),
      };
    });

    const nodes = [...labware, ...tasks];
    return sortBy(nodes, 'id');
  }
);
